/**
 * Detect the Sangoma TeamHub desktop (Electron) app, and whether the running
 * build is new enough to own the meeting window itself.
 *
 * Meet is downloaded fresh on every desktop launch, so a Meet deploy changes
 * behaviour inside desktop apps that are already installed. The Electron shell
 * only changes when the user installs a new build, which is why Meet has to
 * treat "desktop" and "desktop that can host a meeting window" as different
 * things (SM-1186).
 */

declare global {
  interface Window {
    /**
     * Installed by the desktop app's meeting-window preload before any page
     * script runs. Cross-repo contract with teamhub-desktop — do not rename.
     */
    __teamhubMeetingStubInstalled?: boolean;
  }
}

/**
 * Detect the Sangoma TeamHub desktop (Electron) app.
 *
 * The desktop app stamps its user agent with `TeamHubApp/<version>` (see
 * `teamhub-desktop/src/app.window.js`), which is the only signal available to an
 * MF remote running inside the host frame.
 *
 * Meet needs this because the desktop shell must never be replaced by a meeting:
 * there is no browser chrome to navigate back with, so the in-window fallback
 * would strand the user on standalone Meet (SM-1186).
 */
export const isTeamHubDesktop = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /TeamHubApp\//i.test(navigator.userAgent);
};

/**
 * Whether the desktop shell provides the dedicated, app-owned meeting window.
 *
 * Builds with that window install a preload stub that stands in for the opener,
 * which is what carries auth and Meet Settings to the meeting over
 * `postMessage`. Its presence is the only way Meet can tell a current shell from
 * one that predates the feature.
 */
export const hasDesktopMeetingWindow = (): boolean =>
  typeof window !== 'undefined' && window.__teamhubMeetingStubInstalled === true;

/**
 * A desktop shell with no meeting window of its own.
 *
 * These builds route every non-internal URL to the system browser, so Meet must
 * not reserve a popup at all: the reservation opens `about:blank`, which the
 * shell forwards to the OS and Windows escalates to a "Get an app to open this
 * link" Store prompt. Such clients get the external-window fallback instead.
 */
export const isLegacyTeamHubDesktop = (): boolean =>
  isTeamHubDesktop() && !hasDesktopMeetingWindow();
